
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, Paper, Typography, Snackbar, Alert, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { Add, FileDownload, FileUpload, Label as LabelIcon } from '@mui/icons-material';
import DashboardLayout from '../../components/DashboardLayout';
import ContactForm from './ContactForm';
import ContactsTable, { TagOperationsDialog } from './ContactsTable';
import { getContacts, exportContacts, updateContactTags, importContacts, deleteMultipleContacts, getContactTags, deleteContactsByFilter } from '../../services/contactService';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../utils/constants';

const ContactsPage = () => {
  // State management - группируем связанные состояния
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Диалоги
  const [openForm, setOpenForm] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [tagOperationsDialogOpen, setTagOperationsDialogOpen] = useState(false);

  // Состояние для тегов (универсальный подход)
  const [tagOptions, setTagOptions] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);

  useEffect(() => {
    setLoadingTags(true);
    getContactTags()
        .then(tags => {
          setTagOptions(Array.isArray(tags) ? tags : []);
        })
        .catch(() => {
          setTagOptions(['client', 'supplier', 'vip', 'lead', 'partner', 'inactive']);
        })
        .finally(() => setLoadingTags(false));
  }, []);

  // Пагинация - объединяем в один объект
  const [paginationState, setPaginationState] = useState({
    page: DEFAULT_PAGE,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    pages: 0
  });

  // Фильтры - объединяем в один объект
  const [filtersState, setFiltersState] = useState({
    search: '',
    tags: []
  });

  // UI состояние
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [deleteAllFiltered, setDeleteAllFiltered] = useState(false);

  // Мемоизируем обработчик изменения выбора
  const handleSelectionChange = useCallback((selection) => {
    setSelectedContacts(Array.isArray(selection) ? selection : [selection]);
  }, []);

  // Fetch contacts - оптимизированная версия с правильными зависимостями
  const fetchContacts = useCallback(async (customFilters = null, customPagination = null) => {
    setLoading(true);
    setError(null);

    try {
      const filters = customFilters || filtersState;
      const pagination = customPagination || paginationState;

      const response = await getContacts({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });

      if (response?.data) {
        setContacts(response.data);
        setPaginationState({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          pages: response.pagination.pages
        });
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError('Failed to fetch contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [filtersState, paginationState]);

  // Первичная загрузка данных
  useEffect(() => {
    fetchContacts();
  }, []); // Убрали fetchContacts из зависимостей, чтобы избежать бесконечного цикла

  // Обработчики пагинации - мемоизированные
  const handlePageChange = useCallback((newPage) => {
    const newPagination = { ...paginationState, page: newPage };
    setPaginationState(newPagination);
    fetchContacts(null, newPagination);
  }, [paginationState, fetchContacts]);

  const handleRowsPerPageChange = useCallback((newPageSize) => {
    const newPagination = { ...paginationState, limit: newPageSize, page: 1 };
    setPaginationState(newPagination);
    fetchContacts(null, newPagination);
  }, [paginationState, fetchContacts]);

  // Обработчики фильтрации - оптимизированные
  const handleSearch = useCallback((searchTerm) => {
    const newFilters = { ...filtersState, search: searchTerm };
    const newPagination = { ...paginationState, page: 1 };

    setFiltersState(newFilters);
    setPaginationState(newPagination);
    // Конвертируем массив тегов в строку для API
    const apiFilters = {
      ...newFilters,
      tags: Array.isArray(newFilters.tags) ? newFilters.tags.join(', ') : newFilters.tags
    };
    fetchContacts(newFilters, newPagination);
  }, [filtersState, paginationState, fetchContacts]);

  const handleTagsFilter = useCallback((tags) => {
        // Убедимся, что tags всегда массив
        const tagsArray = Array.isArray(tags) ? tags : (tags ? [tags] : []);

        const newFilters = { ...filtersState, tags: tagsArray };
        const newPagination = { ...paginationState, page: 1 };

        setFiltersState(newFilters);
        setPaginationState(newPagination);
        fetchContacts(newFilters, newPagination);
    }, [filtersState, paginationState, fetchContacts]);

  const handleDeleteContacts = useCallback(async () => {
    try {
      setLoading(true);

      const result = await deleteMultipleContacts(selectedContacts);

      setSnackbar({
        open: true,
        message: `Successfully deleted ${result.deletedCount || selectedContacts.length} contacts`,
        severity: 'success'
      });

      // Очистить выбор и обновить данные
      setSelectedContacts([]);
      await fetchContacts();
    } catch (error) {
      console.error('Error deleting contacts:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete contacts: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedContacts, fetchContacts]);

  // Обработчики форм - оптимизированные
  const handleFormOpen = useCallback((contact = null) => {
    setEditContact(contact);
    setOpenForm(true);
  }, []);

  const handleFormClose = useCallback((refresh = false) => {
    setOpenForm(false);
    setEditContact(null);
    if (refresh) {
      fetchContacts();
    }
  }, [fetchContacts]);

  // Обработчики экспорта и импорта - оптимизированные
  const handleExport = useCallback(async (format) => {
    try {
      const apiFilters = {
        ...filtersState,
        tags: Array.isArray(filtersState.tags) ? filtersState.tags.join(', ') : filtersState.tags
      };
      const blob = await exportContacts(format, filtersState);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSnackbar({
        open: true,
        message: `Contacts exported successfully as ${format.toUpperCase()}`,
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to export contacts: ${err.message}`,
        severity: 'error'
      });
    }
  }, [filtersState]);

  const handleImportClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.tsv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          setLoading(true);
          const result = await importContacts(file);

          setSnackbar({
            open: true,
            message: `Contacts imported successfully: ${result.results.created} created, ${result.results.updated} updated`,
            severity: 'success'
          });

          await fetchContacts();
        } catch (error) {
          setSnackbar({
            open: true,
            message: `Failed to import contacts: ${error.message}`,
            severity: 'error'
          });
        } finally {
          setLoading(false);
        }
      }
    };
    input.click();
  }, [fetchContacts]);

  // Обработчики диалога операций с тегами
  const handleTagOperationsOpen = useCallback(() => {
    setTagOperationsDialogOpen(true);
  }, []);

  const handleTagOperationsClose = useCallback(() => {
    setTagOperationsDialogOpen(false);
  }, []);

  const handleDelOperations = useCallback(async () => {
    if (deleteAllFiltered) {
      const total = paginationState.total;
      const message = `ВНИМАНИЕ! Будут удалены ВСЕ контакты, попавшие под текущий фильтр (${total} шт.). Это действие необратимо. Продолжить?`;
      if (window.confirm(message)) {
        try {
          setLoading(true);
          const result = await deleteContactsByFilter(filtersState);
          setSnackbar({ open: true, message: `Удалено ${result.deletedCount || 0} контактов по фильтру`, severity: 'success' });
          setSelectedContacts([]);
          setDeleteAllFiltered(false);
          await fetchContacts();
        } catch (error) {
          console.error('Error deleting by filter:', error);
          setSnackbar({ open: true, message: `Не удалось удалить по фильтру: ${error.message}`, severity: 'error' });
        } finally {
          setLoading(false);
        }
      }
    } else {
      if (window.confirm(`Are you sure you want to delete ${selectedContacts.length} selected contacts?`)) {
        handleDeleteContacts();
      }
    }
  }, [deleteAllFiltered, paginationState.total, filtersState, handleDeleteContacts, fetchContacts]);

  const handleApplyTags = useCallback(async (tagsToApply, operation = 'add') => {
    try {
      setLoading(true);

      const results = await updateContactTags(selectedContacts, tagsToApply, operation);

      if (results.failed?.length > 0) {
        setSnackbar({
          open: true,
          message: `Tags ${operation}ed with some errors. ${results.success.length} succeeded, ${results.failed.length} failed.`,
          severity: 'warning'
        });
      } else {
        setSnackbar({
          open: true,
          message: `Tags ${operation}ed successfully for ${results.success.length} contacts`,
          severity: 'success'
        });
      }

      await fetchContacts();
      handleTagOperationsClose();
    } catch (error) {
      console.error('Error applying tags:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${operation} tags: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedContacts, fetchContacts, handleTagOperationsClose]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Мемоизируем компоненты для предотвращения ненужных рендеров
  const tableComponent = useMemo(() => (
      <ContactsTable
          contacts={contacts}
          loading={loading}
          pagination={paginationState}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSearch={handleSearch}
          onTagsFilter={handleTagsFilter}
          onEdit={handleFormOpen}
          onRefresh={fetchContacts}
          onSelectionChange={handleSelectionChange}
          selectedContacts={selectedContacts}
          onDeleteContacts={handleDeleteContacts}
          tagOptions={tagOptions}
          loadingTags={loadingTags}
          searchValue={filtersState.search}
          selectedTagsValue={filtersState.tags}
      />
  ), [
    contacts,
    loading,
    paginationState,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    handleTagsFilter,
    handleFormOpen,
    fetchContacts,
    handleSelectionChange,
    selectedContacts,
    handleDeleteContacts,
    tagOptions,
    loadingTags,
    filtersState.search,
    filtersState.tags
  ]);

  return (
      <DashboardLayout>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Contacts
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your contacts
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="text.secondary">
            Format: CSV/JSON with columns: name, phone, email, tags, notes
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleFormOpen()}
              sx={{ mr: 1 }}
          >
            Add Contact
          </Button>
          <Button
              variant="contained"
              color="primary"
              startIcon={<LabelIcon />}
              onClick={handleTagOperationsOpen}
              disabled={selectedContacts.length === 0}
              sx={{ mr: 1 }}
          >
            Tags operations
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteAllFiltered}
                  onChange={(e) => setDeleteAllFiltered(e.target.checked)}
                  color="error"
                />
              }
              label={<Typography variant="body2" color={deleteAllFiltered ? 'error' : 'text.secondary'}></Typography>}
              sx={{ mr: 1, userSelect: 'none' }}
            />
            <Tooltip title={deleteAllFiltered ? 'Удалит весь отфильтрованный список' : 'Удалит только выбранные на странице контакты'}>
              <span>
                <Button
                  variant="contained"
                  color={deleteAllFiltered ? 'error' : 'primary'}
                  startIcon={<LabelIcon />}
                  onClick={handleDelOperations}
                  disabled={!deleteAllFiltered && selectedContacts.length === 0}
                  sx={{ mr: 1 }}
                >
                  Удалить
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <Button
                variant="outlined"
                startIcon={<FileUpload />}
                onClick={handleImportClick}
                sx={{ mr: 1 }}
            >
              Import
            </Button>
          </Box>
          <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => handleExport('csv')}
          >
            Export CSV
          </Button>
        </Box>

        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
        )}

        <Paper sx={{ height: '70vh', width: '100%', overflow: 'hidden' }}>
          {tableComponent}
        </Paper>

        <ContactForm
            open={openForm}
            contact={editContact}
            onClose={handleFormClose}
        />

        <TagOperationsDialog
            open={tagOperationsDialogOpen}
            onClose={handleTagOperationsClose}
            onApply={handleApplyTags}
            selectedCount={selectedContacts.length}
            tagOptions={tagOptions}
            loadingTags={loadingTags}
        />

        <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DashboardLayout>
  );
};

export default React.memo(ContactsPage);